import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import greenTick from '../../Assets/Images/FosWallet/success.png';
import transactionProcessing from '../../Assets/Images/FosWallet/transaction_processing.gif';
import transactionFailed from '../../Assets/Images/FosWallet/transaction_failed.gif';
import { useEffect } from 'react';
import successSound from '../../../src/Assets/Images/sound/moneySound.mpeg';

const TransactionStatusModal = ({
  open,
  handleClose,
  transactionDetails: transactionData = [], 
  // <-- default to empty array
}) => {
  const rewardTypeMapping = {
    initial_redeem_bonus: 'Welcome Bonus',
    bonus: 'Loyalty Bonus',
    lead_agreement_done: 'Lead Agreement Reward',
    new_buyer_discount: 'New Buyer Discount',
    existing_buyer_discount: 'Existing Buyer Discount',
  };

  const transformedTransactionData = Array.isArray(transactionData)
    ? transactionData.map((item) => ({
        ...item,
        rewardType: rewardTypeMapping[item.rewardType] || item.rewardType,
        transactionTDS: item.transactionTDS || 'NA',
      }))
    : [];
// console.log('trasaction details',transactionData);

  const allStatuses = transformedTransactionData.map((t) => t.transactionStatus);
  const allSuccess = allStatuses.every((status) => status === 'success');
  const allFailed = allStatuses.every((status) => status === 'failed');
  const allProcessing = allStatuses.every((status) => status === 'processing');
  const containsProcessing = allStatuses.includes('processing');
  const containsSuccess = allStatuses.includes('success');
  const containsFailed = allStatuses.includes('failed');
  const singleTransaction = transformedTransactionData.length === 1;

  let message = '';
  let gif = null;
  let messageColor = '';

  if (allSuccess) {
    message = singleTransaction
      ? 'The transaction was successful.'
      : 'All transactions were successful.';
    gif = greenTick;
    messageColor = 'green';
  } else if (allFailed) {
    message = singleTransaction
      ? 'The transaction failed, please try again later.'
      : 'All transactions failed, please try again later.';
    gif = transactionFailed;
    messageColor = 'red';
  } else if (allProcessing) {
    message = singleTransaction
      ? 'The transaction is in process... '
      : 'All transactions are in process...';
    gif = transactionProcessing;
    messageColor = 'orange';
  } else if (containsSuccess && containsProcessing && containsFailed) {
    message = singleTransaction
      ? 'The transaction is partially completed, with some steps still in progress.'
      : 'Some transactions are successful, some Failed! While others are in progress.';
    gif = transactionProcessing;
    messageColor = 'orange';
  } else if (containsSuccess && containsProcessing) {
    message = singleTransaction
      ? 'The transaction is still processing.'
      : 'Some transactions are successful! While others are still processing.';
    gif = transactionProcessing;
    messageColor = 'orange';
  } else if (containsFailed && containsProcessing) {
    message = singleTransaction
      ? 'The transaction Failed but is still processing.'
      : 'Some transactions are Failed! While others are still processing.';
    gif = transactionProcessing;
    messageColor = 'orange';
  } else if (containsSuccess && containsFailed) {
    message = singleTransaction
      ? 'The transaction is partially successful but encountered some failures.'
      : 'Some transactions are successful! While others have Failed.';
    gif = transactionFailed;
    messageColor = 'red';
  }

  useEffect(() => {
    if (containsSuccess && open) {
      const audio = new Audio(successSound);
      audio.play();
    }
  }, [containsSuccess, open]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 3,
          padding: { xs: '20px', sm: '50px', md: '50px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: 24,
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignSelf: 'stretch',
            width: { md: '95%', xs: '100%' },
            margin: 'auto',
          }}>
          <Typography sx={{ fontSize: '25px', fontFamily: 'rubik', fontWeight: '500' }}>
            Transaction Details
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ textAlign: 'center', mt: '10px' }}>
          {gif && <img src={gif} alt="Status" height="100px" />}
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: '25px',
              fontWeight: '500',
              color: messageColor,
              fontFamily: 'rubik',
              letterSpacing: '0.5px',
            }}>
            {message}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'left', width: '100%', padding: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Rubik',
              fontSize: '12px',
              color: '#757575',
              padding: 0,
            }}>
            <ul style={{ textAlign: 'left' }}>
              <li>TDS of 10% will be deducted from the amount.</li>
              {(containsSuccess || containsProcessing) && (
                <li>The amount will be credited to your account within 48 hours.</li>
              )}
            </ul>
          </Typography>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ marginTop: '20px', width: { md: '95%', xs: '100%' } }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Reward Type', 'Amount', 'TDS', 'Status', 'UTR'].map((head) => (
                  <TableCell
                    key={head}
                    sx={{ fontFamily: 'rubik', fontWeight: 500, fontSize: 18,  lineHeight: 'normal' }}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {transformedTransactionData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontFamily: 'rubik', fontWeight: 450, fontSize: 15 }}>
                    {row.rewardType}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'rubik', fontWeight: 450, fontSize: 15 }}>
                    {row.transactionAmount}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'rubik', fontWeight: 450, fontSize: 15 }}>
                    {row.transactionTDS}
                  </TableCell>
                  <TableCell
                    sx={{
                      color:
                        row.transactionStatus === 'success'
                          ? 'green'
                          : row.transactionStatus === 'processing'
                          ? 'orange'
                          : 'red',
                      fontFamily: 'rubik',
                      fontWeight: 500,
                      fontSize: 16,
                    }}>
                    {row.transactionStatus?.toUpperCase()}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'rubik', fontWeight: 450, fontSize: 15 }}>
                    {row.transactionUTR}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Dialog>
  );
};

export default TransactionStatusModal;
